// Schema-driven configuration for the Blueprint Spec Sheet.
// The default below mirrors the original hardcoded form; admins can override
// it via the Form Admin editor (persisted server-side).

export const CONFIG_KEY = "blueprint_spec_sheet";

// Where each section renders. Sections keep their relative order within an area.
export const AREA_LABELS = {
  main: "Main Form",
  roofs: "Roofs Modal",
  walls: "Wall Panels Modal",
  walls_levels: "Wall Panels Modal — Per Level",
  floors: "Floors Modal — Additional Details",
};

export const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Text Area" },
  { value: "select", label: "Dropdown" },
  { value: "multiselect", label: "Multi-Select" },
  { value: "date", label: "Date" },
];

export const DEFAULT_SPEC_SHEET_CONFIG = {
  key: CONFIG_KEY,
  version: 1,
  sections: [
    {
      id: "contact",
      area: "main",
      title: "Who is this for?",
      description: "Contractor and account information.",
      columns: 3,
      fields: [
        { id: "contractorName", label: "Contractor Name", type: "text", required: false },
        { id: "residentName", label: "Resident Name", type: "text", required: false },
        { id: "accountNumber", label: "Account Number", type: "text", required: false },
      ],
    },
    {
      id: "location",
      area: "main",
      title: "Job Location",
      description: "Enter the address, or drop a pin on the map if there isn't one.",
      columns: 3,
      fields: [
        {
          id: "address",
          label: "Address",
          type: "text",
          required: true,
          special: "address",
          locked: true,
        },
        {
          id: "state",
          label: "State",
          type: "select",
          required: false,
          options: ["Indiana", "Michigan", "Ohio"],
        },
      ],
    },
    {
      id: "project",
      area: "main",
      title: "Project Details",
      description: "What kind of job is this and when is it needed?",
      columns: 3,
      fields: [
        {
          id: "buildingType",
          label: "Building Type",
          type: "select",
          required: false,
          options: [
            "Residential",
            "Commercial - Design Build",
            "Commercial - Spec Book",
            "Pole Barn",
            "Agriculture(Low hazard to human life in failure)",
          ],
        },
        {
          id: "bidType",
          label: "Bid Type",
          type: "select",
          required: false,
          options: ["Bid: 2-5 Day +/-", "Bid with Birdseye View: 7-10 Days +/-"],
        },
        {
          id: "otherBidders",
          label: "Other Bidders",
          type: "select",
          required: false,
          options: ["ALH", "Alpha"],
        },
        {
          id: "quoteOrder",
          label: "Quote / Order",
          type: "select",
          required: false,
          options: ["Quote", "Order"],
        },
        {
          id: "taxExempt",
          label: "Tax Exempt?",
          type: "select",
          required: false,
          options: ["Yes", "No"],
          defaultValue: "No",
        },
        {
          id: "requiredDate",
          label: "Required Date",
          type: "date",
          required: false,
          special: "date_asap",
          locked: true,
        },
      ],
    },
    {
      id: "products",
      area: "main",
      title: "Products Needing Quotes",
      description:
        "Select each product type to quote, then open it from the to-do list to fill in the details.",
      special: "products",
      fields: [],
    },
    {
      id: "notes",
      area: "main",
      title: "Notes",
      description: "Anything else we should know about this job (optional).",
      columns: 1,
      fields: [{ id: "notes", label: "Notes", type: "textarea", required: false }],
    },

    // ---- Roofs modal ----
    {
      id: "roof_structure",
      area: "roofs",
      title: "Structure & Loading",
      columns: 3,
      fields: [
        {
          id: "Loading",
          label: "Loading",
          type: "select",
          required: true,
          options: [
            "67 (50-7-10)",
            "52 (35-7-10)",
            "47 (30-7-10)",
            "37 (20-7-10)",
            "60 (50-5-5)",
            "45 (35-5-5)",
            "40 (30-5-5)",
            "30 (20-5-5)",
            "Other",
          ],
        },
        {
          id: "RoofPitch",
          label: "Roof Pitch",
          type: "select",
          required: true,
          options: [
            "Per Print",
            "3/12",
            "4/12",
            "5/12",
            "6/12",
            "7/12",
            "8/12",
            "9/12",
            "10/12",
            "11/12",
            "12/12",
            "Other",
          ],
        },
        {
          id: "HeelHeight",
          label: "Heel Height",
          type: "select",
          required: true,
          special: "heel_height",
          locked: true,
          options: ["Per Print", "Common Heel", "Raised Heel"],
        },
        {
          id: "OnCenterSpacing",
          label: "On Center Spacing",
          type: "select",
          required: true,
          options: ['12"', '16"', '19.2"', '24"', '48"', "Other (give a field to explain)"],
        },
        {
          id: "BearingWidth",
          label: "Bearing Width",
          type: "select",
          required: true,
          options: [
            '3-1/2"',
            '5-1/2"',
            '7-1/4"',
            '8-1/2"',
            '9-1/4"',
            "Per Print",
            "Various/Other (give a field to explain)",
          ],
        },
        {
          id: "Overhang",
          label: "Overhang",
          type: "select",
          required: true,
          options: [
            "None",
            "Per Print",
            '8"',
            '10-1/2"',
            '12"',
            '12-1/2"',
            '14"',
            '14-1/2"',
            '16"',
            '16-1/2"',
            '18"',
            '22-1/2"',
            '24"',
            '36"',
            "Other",
          ],
        },
      ],
    },
    {
      id: "roof_material",
      area: "roofs",
      title: "Roofing & Ceiling",
      columns: 3,
      fields: [
        {
          id: "RoofingMaterial",
          label: "Roofing Material",
          type: "select",
          required: true,
          defaultValue: "Asphalt Shingles",
          options: [
            "Asphalt Shingles",
            "Wood Shakes",
            'Slate Tile 3/8"',
            "Metal 20 ga.",
            "Metal 18 ga.",
            "Other",
          ],
        },
        {
          id: "CeilingCondition",
          label: "Ceiling Condition",
          type: "select",
          required: true,
          options: ["All Flat", "Per Print", "Other"],
        },
        {
          id: "CeilingPitch",
          label: "Ceiling Pitch",
          type: "select",
          required: true,
          defaultValue: "Half Roof Pitch",
          options: [
            "Per Print",
            "Half Roof Pitch",
            "Most Economical",
            "High as possible (without adjusting Heel)",
            "Various/Other (give a field to explain)",
          ],
        },
        {
          id: "HeatedBuilding",
          label: "Heated Building",
          type: "select",
          required: true,
          options: ["Yes", "No"],
        },
      ],
    },
    {
      id: "roof_truss",
      area: "roofs",
      title: "Truss Details",
      columns: 3,
      fields: [
        {
          id: "TrussTieDowns",
          label: "Truss Tie Downs",
          type: "select",
          required: true,
          defaultValue: "Most Economical",
          options: [
            "H2.5T",
            "H2.5A",
            "MTS12",
            "SDWC15600 Screws",
            "Most Economical",
            "None",
            "Other",
          ],
        },
        {
          id: "PiggyBackType",
          label: "Piggy Back Type",
          type: "select",
          required: true,
          defaultValue: "Standard Piggy Back",
          options: ["Standard Piggy Back", "Hinge Plate", "Option for both", "None"],
        },
        {
          id: "DropGable",
          label: "Drop Gable",
          type: "select",
          required: true,
          defaultValue: "None",
          options: [
            '2x Flat (drop 1-1/2")',
            '2x4 (drop 3-1/2")',
            '2x6 (drop 5-1/2")',
            '2x8 (drop 7-1/4")',
            '2x10 (drop 9-1/4")',
            "None",
          ],
        },
        {
          id: "AtticStorage",
          label: "Attic Storage",
          type: "select",
          required: true,
          defaultValue: "None",
          options: [
            "None",
            "Spread Web",
            "Attic Truss - Hay",
            "Attic Truss - Commercial",
            "Attic Truss - Residential",
            "Attic Truss - Not on print (give a field to explain - Note we need location and use)",
          ],
        },
        {
          id: "TrussSlidingClips",
          label: "Truss Sliding Clips",
          type: "select",
          required: true,
          defaultValue: "None",
          options: ["DTC", "STC", "STCT", "None"],
        },
      ],
    },
    {
      id: "roof_extras",
      area: "roofs",
      title: "Extras & Options",
      columns: 3,
      fields: [
        {
          id: "ValleySets",
          label: "Valley Sets",
          type: "select",
          required: true,
          defaultValue: "No",
          options: ["Yes", "No", "As An Option"],
        },
        {
          id: "Dormer",
          label: "Dormer",
          type: "select",
          required: true,
          options: ["Per Print", "None", "Live - Hole in Roof", "False - Overbuild - Fake"],
        },
        {
          id: "GirderLags",
          label: "Girder Lags",
          type: "select",
          required: true,
          defaultValue: "No",
          options: ["Yes", "No", "As An Option"],
        },
        {
          id: "CornerSets",
          label: "Corner Sets",
          type: "select",
          required: true,
          defaultValue: "No",
          options: ["Yes", "No", "As An Option"],
        },
        {
          id: "LayInGables",
          label: "Lay In Gables",
          type: "select",
          required: true,
          defaultValue: "No",
          options: ["Yes", "No", "As An Option"],
        },
      ],
    },

    // ---- Wall Panels modal ----
    {
      id: "wall_basic",
      area: "walls",
      title: "Basic Wall Information",
      columns: 3,
      fields: [
        {
          id: "wallHeight",
          label: "Wall Height",
          type: "multiselect",
          required: true,
          locationsWhenMultiple: true,
          options: ["8' 1-1/8\"", "9' 1-1/8\"", "10' 1-1/8\"", "11' 1-1/8\"", "12' 1-1/8\""],
        },
        {
          id: "interiorWallWidth",
          label: "Interior Wall Width",
          type: "multiselect",
          required: true,
          locationsWhenMultiple: true,
          options: ['3-1/2"', '5-1/2"', '7-1/4"', '8-1/2"', '9-1/4"', "Per Print"],
        },
        {
          id: "exteriorWallWidth",
          label: "Exterior Wall Width",
          type: "multiselect",
          required: true,
          locationsWhenMultiple: true,
          options: ['3-1/2"', '5-1/2"', '7-1/4"', '8-1/2"', '9-1/4"', "Per Print"],
        },
        {
          id: "garageWallWidth",
          label: "Garage Wall Width",
          type: "multiselect",
          required: true,
          locationsWhenMultiple: true,
          options: ['3-1/2"', '5-1/2"', '7-1/4"', '8-1/2"', '9-1/4"', "Per Print"],
        },
        {
          id: "wallSheathing",
          label: "Wall Sheathing",
          type: "select",
          required: true,
          options: [
            "OPTION A Outside of sheathing line up with outside of Foundation",
            "OPTION B Outside of 2x wall line up with outside of Foundation",
          ],
        },
      ],
    },
    {
      id: "wall_levels",
      area: "walls_levels",
      title: "Framing & Sheathing by Level",
      columns: 3,
      fields: [
        {
          id: "studSpacing",
          label: "Stud Spacing",
          type: "select",
          required: false,
          options: ['12"', '16"', '19.2"', '24"', '48"', "Other"],
        },
        {
          id: "sheathing",
          label: "Sheathing",
          type: "select",
          required: false,
          options: ['7/16" OSB', '1/2" OSB', '7/16" ZIP', '1/2" ZIP', '1/2" Plywood', "Other"],
        },
        {
          id: "sheathingInstallation",
          label: "Sheathing Installation",
          type: "select",
          required: false,
          options: [
            "Include Loose",
            "Include attached with Staples",
            "Include attached with Nails",
            "Do not Include in Package",
          ],
        },
      ],
    },

    // ---- Floors modal (additional details; the level/material table is built-in) ----
    {
      id: "floor_additional",
      area: "floors",
      title: "Additional Floor Details",
      columns: 2,
      fields: [
        {
          id: "SillPlateSize",
          label: "Sill Plate Size",
          type: "multiselect",
          required: true,
          locationPerSelection: true,
          options: [
            '3-1/2"',
            '5-1/2"',
            '7-1/4"',
            '9-1/4"',
            "Per Print",
            "Various/Other (give a field to explain)",
          ],
        },
        {
          id: "FirePlace",
          label: "Fire Place",
          type: "select",
          required: true,
          options: ["NONE", "Pre-Fabricated", "Pre-Fab with Stone Face", " Masonry( Hole in Floor )"],
        },
        {
          id: "CeramicTileFlooring",
          label: "Ceramic Tile Flooring",
          type: "multiselect",
          required: true,
          options: ["NONE", "Whole House", "Kitchen", "Bathrooms", "Laundry", "Other"],
        },
        {
          id: "Hardwood",
          label: "Hardwood",
          type: "multiselect",
          required: true,
          options: [
            "NONE",
            "Whole House",
            "Kitchen",
            "Bathrooms",
            "Laundry",
            "Bedrooms",
            "Living Room",
            "Dining Room",
            "Master Bedroom",
            "Family Room",
            "Other",
          ],
        },
        {
          id: "CustomFlooring",
          label: "Custom Flooring",
          type: "multiselect",
          required: true,
          options: [
            "NONE",
            "Whole House",
            "Kitchen",
            "Bathrooms",
            "Laundry",
            "Bedrooms",
            "Living Room",
            "Dining Room",
            "Master Bedroom",
            "Family Room",
            "Other",
          ],
        },
        {
          id: "CustomCountertops",
          label: "Custom Countertops",
          type: "multiselect",
          required: true,
          options: ["NONE", "Kitchen", "Bathrooms", "Laundry", "Master Closet", "Bar Area", "Other"],
        },
        {
          id: "LargeObjects",
          label: "Large Objects",
          type: "multiselect",
          required: true,
          options: [
            "NONE",
            "Kitchen",
            "Bathroom",
            "Laundry",
            "Bedroom",
            "Living Room",
            "Dining Room",
            "Master Bedroom",
            "Family Room",
            "Other",
          ],
        },
      ],
    },
  ],
};

export const sectionsForArea = (config, area) =>
  (config?.sections || []).filter((s) => s.area === area);

export const fieldsForArea = (config, area) =>
  sectionsForArea(config, area).flatMap((s) => s.fields || []);

// Seed values (e.g. Tax Exempt -> "No") declared on the config
export const defaultValuesForArea = (config, area) => {
  const out = {};
  fieldsForArea(config, area).forEach((f) => {
    if (f.defaultValue !== undefined) out[f.id] = f.defaultValue;
  });
  return out;
};

// A single field's completeness, including its companion inputs
// ("Other" specify boxes and location fields for multi-selects).
export const isFieldComplete = (field, values) => {
  const v = values?.[field.id];

  if (field.type === "multiselect") {
    const selected = Array.isArray(v) ? v : [];
    if (field.required && selected.length === 0) return false;
    if (selected.includes("Other") && !values?.[`${field.id}_Other`]) return false;
    if (field.locationPerSelection && selected.length > 1) {
      if (!selected.every((sel) => values?.[`${field.id}_locations`]?.[sel])) return false;
    }
    if (field.locationsWhenMultiple && selected.length > 1) {
      if (!values?.[`${field.id}Location`]) return false;
    }
    return true;
  }

  if (!field.required) return true;
  return v !== undefined && v !== null && String(v).trim() !== "";
};

export const areFieldsComplete = (fields, values) =>
  (fields || []).every((f) => isFieldComplete(f, values));

// "2026-07-15" -> "07-15-2026"; anything else ("ASAP", free text) passes through
export const formatSpecDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  return m ? `${m[2]}-${m[3]}-${m[1]}` : value;
};

export const makeFieldId = (label) => {
  const slug = String(label || "field")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  return `${slug || "field"}_${Math.random().toString(36).slice(2, 7)}`;
};
